import { z } from "zod";
import { permissionCatalogEntrySchema } from "./organizations.js";
import { idSchema, multiLineTextSchema, singleLineTextSchema } from "./text.js";
// Ruoli dell'azienda (piano M1a Task 11, D4; decisioni del controllore R3, R10, R16). Aree, azioni, ambiti e
// funzioni sono stringhe qui: il server le confronta con il catalogo di `@gioia365/db` (aree e azioni
// sconosciute → `422 validation_failed`; `create` soltanto `none`/`all`; aree di sola lettura soltanto con
// `view`; una funzione fuori catalogo o non disponibile alle aziende → `422 feature_unavailable`). Il livello
// non si modifica: un ruolo su misura lo eredita dal ruolo da cui è copiato. Le chiavi del catalogo non arrivano
// mai a una query così come sono: nel database va soltanto ciò che il confronto con il catalogo riconosce.
// Il catalogo dei permessi, come lo conosce l'interfaccia (che non importa `@gioia365/db`, decisione R10 di M1a):
// soltanto le chiavi, nello stesso ordine del catalogo del server. Servono a tipizzare le etichette di testo, così
// una voce senza etichetta non compila. `apps/server/test/catalog-parity.test.ts` confronta queste liste con
// `PERMISSION_AREAS`, `PERMISSION_ACTIONS`, `PERMISSION_SCOPES`, `VIEW_ONLY_AREAS` e `FEATURE_KEYS` di
// `@gioia365/db`: una funzione nuova nel catalogo senza la sua voce qui fa fallire la CI. Gli schemi zod restano a
// stringhe: soltanto il server valida contro il catalogo.
export const PERMISSION_AREA_KEYS = ["leads", "smart_lists", "tasks", "opportunities", "conversations", "activity", "reports"];
export const PERMISSION_ACTION_KEYS = ["view", "create", "update", "delete"];
export const PERMISSION_SCOPE_KEYS = ["none", "own", "all"];
/** Aree che si possono soltanto vedere. */
export const VIEW_ONLY_AREA_KEYS = ["conversations", "activity", "reports"];
export const FEATURE_KEYS_CATALOG = [
    "change_owner",
    "import_leads",
    "export_leads",
    "bulk_actions",
    "merge_duplicates",
    "trash_restore",
    "manage_tags",
    "call",
    "whatsapp_send",
    "listen_team_recordings",
    "commercial_view",
    "manage_team",
    "manage_roles",
    "settings.team",
    "settings.roles",
    "settings.phone_numbers",
    "settings.lead_statuses",
    "settings.lead_fields",
    "settings.lead_visibility",
    "settings.tags",
    "settings.call_sequence",
    "settings.outcomes",
    "settings.callbacks",
    "settings.whatsapp_channels",
    "settings.whatsapp_sending",
    "settings.whatsapp_templates",
    "settings.calendar",
    "settings.assignment",
    "settings.lead_intake",
    "settings.table_views",
    "settings.goals",
];
const catalogKey = z.string().min(1).max(64);
const roleName = singleLineTextSchema(80);
const description = multiLineTextSchema(500);
/** Area → azione → ambito; un'area o un'azione assente vale `none`. */
export const roleAreaPermissionsSchema = z.record(catalogKey, z.record(catalogKey, catalogKey));
export const organizationRoleSchema = z.object({
    id: z.string(),
    name: z.string(),
    description: z.string().nullable(),
    level: z.number().int().min(1).max(4),
    isSystem: z.boolean(),
    areaPermissions: roleAreaPermissionsSchema,
    features: z.array(z.string()),
    createdFromRoleId: z.string().nullable(),
    archivedAt: z.iso.datetime().nullable(),
});
/** Ciò che la schermata «Ruoli e permessi» può offrire, con la disponibilità decisa dalla piattaforma. */
export const rolesCatalogSchema = z.object({
    areas: z.array(z.string()),
    actions: z.array(z.string()),
    scopes: z.array(z.string()),
    viewOnlyAreas: z.array(z.string()),
    features: z.array(permissionCatalogEntrySchema),
});
/** Risposta di `GET /api/roles` (`?includeArchived=true` aggiunge i ruoli archiviati). */
export const rolesResponseSchema = z.object({ roles: z.array(organizationRoleSchema), catalog: rolesCatalogSchema });
export const roleResponseSchema = z.object({ role: organizationRoleSchema });
/** Corpo di `POST /api/roles`: permessi, funzioni e livello vengono dal ruolo di partenza. */
export const createRoleBodySchema = z.strictObject({
    name: roleName,
    description: description.optional(),
    copyFromRoleId: idSchema,
});
/** Corpo di `PATCH /api/roles/:id`: almeno un campo; `areaPermissions` e `features` sostituiscono i precedenti; `description: null` la toglie. */
export const updateRoleBodySchema = z
    .strictObject({
    name: roleName.optional(),
    description: description.nullable().optional(),
    areaPermissions: roleAreaPermissionsSchema.optional(),
    features: z.array(catalogKey).max(100).optional(),
})
    .refine((body) => body.name !== undefined || body.description !== undefined || body.areaPermissions !== undefined || body.features !== undefined, { message: "At least one field is required" });
//# sourceMappingURL=roles.js.map